/**
 * Corporate Configuration – Clinic configuration master for corporate details.
 * Uses HISTOPATHOLOGY_CORPORATE_CONFIG (POST create/update, GET list, DELETE).
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Colors } from "../../Styles/Colors";
import CommonValidation from "../../Utility/CommonValidation";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import {
  CommonEditButton,
  CommonGridHeader,
  DeletePopup,
} from "../../Components/Common Components/CommonComponents";
import { ImagePaths } from "../../Utility/ImagePaths";
import "../../Styles/Configuration.css";

const initialForm = {
  RegisteredName: "",
  GSTIN: "",
  Address01: "",
  Address02: "",
  City: "",
  Pincode: "",
  State: "",
  Country: "",
  EmployeeRangeFrom: "",
  EmployeeRangeTo: "",
  EmailID: "",
  MobileNumber: "",
  DiscountPercent: "",
  DiscountNotes: "",
};

export default function CorporateConfiguration() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [CorporateList, setCorporateList] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState(false);
  const [SelectedList, setSelectedList] = useState(null);
  const [isLoader, setIsLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");

  const setLoader = (key) => setIsLoader(key);
  const errorMessage = (message) => {
    setIsErrorMsg(true);
    setIsErrorMsgText(message);
  };
  const successMessage = (message) => {
    setSuccessMsg(true);
    setSuccessMsgText(message);
  };
  const msgClose = useCallback(() => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setSuccessMsg(false);
    setSuccessMsgText("");
  }, []);

  const getPincodeToCity = useCallback((pincode) => {
    if (!pincode || pincode.length !== 6) return;
    fetch("https://api.postalpincode.in/pincode/" + pincode)
      .then((res) => res.json())
      .then((response) => {
        if (response && response[0]?.Status === "Success") {
          const data = response[0].PostOffice;
          if (data && data.length > 0) {
            setForm((prev) => ({
              ...prev,
              City: prev.City || data[0].District,
              State: prev.State || data[0].State,
              Country: prev.Country || data[0].Country,
            }));
          }
        }
      })
      .catch(() => {});
  }, []);

  const GetCorporateList = useCallback((searchKey) => {
    setLoader(true);
    const url = searchKey
      ? `${Serviceurls.HISTOPATHOLOGY_CORPORATE_CONFIG}?search_key=${encodeURIComponent(searchKey)}`
      : Serviceurls.HISTOPATHOLOGY_CORPORATE_CONFIG;
    RestAPIService.getAll(url)
      .then((response) => {
        if (response?.data?.status === "success") {
          setCorporateList(response.data.data ?? []);
        } else {
          errorMessage(response?.data?.message || "Failed to load");
        }
        setLoader(false);
      })
      .catch((error) => {
        setLoader(false);
        errorMessage(error?.response?.data?.message || error?.message);
      });
  }, []);

  useEffect(() => {
    GetCorporateList();
  }, [GetCorporateList]);

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    const alpha = CommonValidation.alphabetOnly(value);
    const number = CommonValidation.numberOnly(value);
    const GST = CommonValidation.gstnumberValidation(value);
    const Discount = CommonValidation.DecimalNumber(value);

    if (name === "City" || name === "Country" || name === "State") {
      if (alpha || value === "") setForm((prev) => ({ ...prev, [name]: value }));
    } else if (
      name === "Pincode" ||
      name === "EmployeeRangeFrom" ||
      name === "EmployeeRangeTo" ||
      name === "MobileNumber"
    ) {
      if (number || value === "") {
        setForm((prev) => {
          const next = { ...prev, [name]: value };
          if (name === "Pincode" && value.length === 6) getPincodeToCity(value);
          return next;
        });
      }
    } else if (name === "GSTIN") {
      if (GST || value === "") setForm((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    } else if (name === "DiscountPercent") {
      if ((Discount && +value <= 100) || value === "") setForm((prev) => ({ ...prev, [name]: value }));
    } else if (name === "DiscountNotes") {
      setForm((prev) => ({ ...prev, [name]: value }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const ClearData = useCallback(() => {
    setForm(initialForm);
    setIsEditClicked(false);
    setIsDeleteClicked(false);
    setSelectedList(null);
  }, []);

  const validateRange = (From, To) => {
    if (From === "" && To === "") return true;
    if (From !== "" && To === "") return false;
    if (From === "" && To !== "") return false;
    if (From !== "" && To !== "") {
      if (+From > +To) return false;
      if (+From === 0 && +To === 0) return false;
      return true;
    }
    return true;
  };

  const postCorporate = useCallback(() => {
    const data = {
      company_name: CommonValidation.removeSpace(form.RegisteredName),
      gst_no: form.GSTIN,
      address_line_1: CommonValidation.removeSpace(form.Address01),
      address_line_2: CommonValidation.removeSpace(form.Address02),
      pincode: String(form.Pincode || "").trim(),
      city: CommonValidation.removeSpace(form.City),
      state: CommonValidation.removeSpace(form.State),
      country: CommonValidation.removeSpace(form.Country),
      employee_range_from: String(form.EmployeeRangeFrom || "").trim(),
      employee_range_to: String(form.EmployeeRangeTo || "").trim(),
      email: CommonValidation.removeSpace(form.EmailID),
      mobile: form.MobileNumber || "",
      discount_percent: Number(form.DiscountPercent) || 0,
      discount_notes: CommonValidation.removeSpace(form.DiscountNotes) || "",
    };
    if (isEditClicked && SelectedList?.id) data.id = SelectedList.id;
    setLoader(true);
    RestAPIService.create(data, Serviceurls.HISTOPATHOLOGY_CORPORATE_CONFIG)
      .then((response) => {
        if (response?.data?.status === "success") {
          ClearData();
          GetCorporateList();
          setLoader(false);
          successMessage(response.data.message);
        } else {
          setLoader(false);
          errorMessage(response?.data?.message || "Failed to save");
        }
      })
      .catch((error) => {
        setLoader(false);
        errorMessage(
          error?.response?.data?.status === "fail"
            ? error?.response?.data?.message
            : error?.message
        );
      });
  }, [form, isEditClicked, SelectedList, ClearData, GetCorporateList]);

  const validation = () => {
    const Email = CommonValidation.emailValidation(form.EmailID);
    const Rangevalidation = validateRange(form.EmployeeRangeFrom, form.EmployeeRangeTo);
    const SameCorpName = CorporateList.filter(
      (list) => list.company_name?.toLowerCase() === form.RegisteredName?.toLowerCase()
    );
    const duplicateOk =
      SameCorpName.length === 0 ||
      (SameCorpName.length === 1 && SameCorpName[0].id === SelectedList?.id);

    if (
      Email &&
      CommonValidation.removeSpace(form.RegisteredName) !== "" &&
      form.GSTIN.length === 15 &&
      CommonValidation.removeSpace(form.Address01) !== "" &&
      CommonValidation.removeSpace(form.City) !== "" &&
      form.Pincode.length === 6 &&
      +form.Pincode !== 0 &&
      CommonValidation.removeSpace(form.State) !== "" &&
      CommonValidation.removeSpace(form.Country) !== "" &&
      form.MobileNumber.length === 10 &&
      form.DiscountPercent !== "" &&
      Rangevalidation &&
      duplicateOk
    ) {
      postCorporate();
      return;
    }

    if (CommonValidation.removeSpace(form.RegisteredName) === "") errorMessage("Enter company name.");
    else if (form.GSTIN.length !== 15) errorMessage("Enter valid GSTIN (15 characters).");
    else if (CommonValidation.removeSpace(form.Address01) === "") errorMessage("Enter address line 1.");
    else if (CommonValidation.removeSpace(form.City) === "") errorMessage("Enter city.");
    else if (form.Pincode.length !== 6 || +form.Pincode === 0) errorMessage("Enter valid 6-digit pincode.");
    else if (CommonValidation.removeSpace(form.State) === "") errorMessage("Enter state.");
    else if (CommonValidation.removeSpace(form.Country) === "") errorMessage("Enter country.");
    else if (!Email) errorMessage("Enter valid email.");
    else if (form.MobileNumber.length !== 10) errorMessage("Enter valid 10-digit mobile number.");
    else if (form.DiscountPercent === "") errorMessage("Enter discount percent.");
    else if (form.EmployeeRangeFrom !== "" && form.EmployeeRangeTo === "") errorMessage("Enter employee range To.");
    else if (form.EmployeeRangeTo !== "" && form.EmployeeRangeFrom === "") errorMessage("Enter employee range From.");
    else if (parseInt(form.EmployeeRangeFrom, 10) > parseInt(form.EmployeeRangeTo, 10)) errorMessage("Invalid employee range.");
    else if (form.EmployeeRangeFrom === "0" && form.EmployeeRangeTo === "0") errorMessage("Invalid employee range.");
    else if (!duplicateOk) errorMessage("Company name already exists.");
  };

  const onEditHandler = (params) => {
    const row = params.row;
    setForm({
      RegisteredName: row.company_name || "",
      GSTIN: row.gst_no || "",
      Address01: row.address_line_1 || "",
      Address02: row.address_line_2 || "",
      City: row.city || row.city_name || "",
      Pincode: row.pincode ?? "",
      State: row.state || row.state_name || "",
      Country: row.country || "",
      EmployeeRangeFrom: row.employee_range_from ?? "",
      EmployeeRangeTo: row.employee_range_to ?? "",
      EmailID: row.email || "",
      MobileNumber: row.mobile ?? row.mobile_no ?? "",
      DiscountPercent: row.discount_percent ?? "",
      DiscountNotes: row.discount_notes ?? "",
    });
    setIsEditClicked(true);
    setSelectedList(row);
  };

  const onDeleteHandler = (params) => {
    setSelectedList(params.row);
    setIsDeleteClicked(true);
  };

  const popupClose = () => setIsDeleteClicked(false);

  const DeleteCorporate = () => {
    if (!SelectedList?.id) return;
    const urlWithId = `${Serviceurls.HISTOPATHOLOGY_CORPORATE_CONFIG}?id=${SelectedList.id}`;
    RestAPIService.delete(urlWithId)
      .then((response) => {
        if (response?.data?.status === "success") {
          successMessage(response.data.message);
          ClearData();
          GetCorporateList();
        } else {
          errorMessage(response?.data?.message || "Failed to delete");
        }
      })
      .catch((error) => errorMessage(error?.response?.data?.message || error?.message));
    popupClose();
  };

  const renderTextBox = (label, value, name, width, maxLength, mandatory) => (
    <TextField
      fullWidth
      inputProps={{ maxLength }}
      sx={{ marginTop: "0.8dvw" }}
      size="small"
      label={mandatory ? label + " *" : label}
      name={name}
      value={value}
      onChange={onChangeHandler}
    />
  );

  const rows = CorporateList.map((el, index) => ({ ...el, sno: index + 1 }));

  const deleteImg = ImagePaths?.LabDeleteButton ?? ImagePaths?.Delete ?? ImagePaths?.LabDeleteButton?.default ?? ImagePaths?.Delete?.default;

  const columns = [
    { field: "sno", sortable: false, flex: 0.08, headerName: "S.No", minWidth: 60 },
    { field: "company_name", flex: 0.21, headerName: "Company Name", minWidth: 140 },
    {
      field: "mobile_no",
      flex: 0.166,
      headerName: "Mobile Number",
      minWidth: 110,
      valueGetter: (value, row) => row.mobile ?? row.mobile_no ?? value,
    },
    { field: "gst_no", flex: 0.21, headerName: "GSTIN", headerAlign: "center", align: "center", minWidth: 130 },
    { field: "discount_percent", flex: 0.08, headerName: "Discount %", minWidth: 80 },
    {
      field: "action",
      flex: 0.166,
      headerName: "Action",
      headerAlign: "center",
      align: "center",
      sortable: false,
      minWidth: 120,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
          <CommonEditButton size="1.5dvw" disable={false} onClick={() => onEditHandler(params)} />
          <Box
            component="img"
            src={typeof deleteImg === "object" && deleteImg?.default ? deleteImg.default : deleteImg}
            alt="Delete"
            sx={{ width: "1.5dvw", height: "1.5dvw", cursor: "pointer" }}
            onClick={() => onDeleteHandler(params)}
          />
        </Stack>
      ),
    },
  ];

  return (
    <Box component="div" className="eMed_refdoc_container">
      <Grid container spacing={2} className="eMed_refdoc_content">
        <Grid item xs={8}>
          <Stack component="div">
            <Paper className="eMed_refdoc_left">
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id ?? row.sno}
                page={page}
                pageSize={pageSize}
                onPaginationModelChange={(model) => {
                  setPage(model.page);
                  setPageSize(model.pageSize);
                }}
                paginationModel={{ page, pageSize }}
                hideFooterSelectedRowCount
                localeText={{
                  toolbarColumns: "",
                  toolbarDensity: "",
                  toolbarExport: "",
                  toolbarFilters: "",
                  toolbarExportPrint: "",
                }}
                slots={{
                  toolbar: CommonGridHeader.CommonToolBar,
                  noRowsOverlay: () => (
                    <Stack className="eMed_conf_nodata">No Records Found</Stack>
                  ),
                }}
                slotProps={{
                  toolbar: { showQuickFilter: true },
                }}
                rowHeight={40}
                columnHeaderHeight={40}
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                    color: "#616161",
                    fontSize: "0.9dvw",
                  },
                  "& .MuiDataGrid-cell": { fontSize: "0.9dvw" },
                }}
                pageSizeOptions={[10, 20, 30]}
                pagination
                disableRowSelectionOnClick
                loading={isLoader}
              />
            </Paper>
          </Stack>
        </Grid>
        <Grid item xs={4} sx={{ minWidth: 0 }}>
          <Stack component="div" sx={{ width: "100%" }}>
            <Paper className="eMed_refdoc_right" elevation={2} sx={{ width: "100%", boxSizing: "border-box" }}>
              <Typography className="eMed_refdoc_title">Corporate Details</Typography>
              <Grid container className="eMed_corpconfig_txt_card" spacing={1.5}>
                <Grid item xs={12}>
                  {renderTextBox("Registered Name", form.RegisteredName, "RegisteredName", "27.5vw", 50, true)}
                </Grid>
                <Grid item xs={12}>
                  {renderTextBox("GSTIN", form.GSTIN, "GSTIN", "27.5vw", 15, true)}
                </Grid>
                <Grid item xs={6}>
                  {renderTextBox("Address Line 1", form.Address01, "Address01", "13.5vw", 50, true)}
                </Grid>
                <Grid item xs={6}>
                  {renderTextBox("Address Line 2", form.Address02, "Address02", "13.5vw", 50, false)}
                </Grid>
                <Grid item xs={6}>
                  {renderTextBox("Pincode", form.Pincode, "Pincode", "13.5vw", 6, true)}
                </Grid>
                <Grid item xs={6}>
                  {renderTextBox("City", form.City, "City", "13.5vw", 50, true)}
                </Grid>
                <Grid item xs={6}>
                  {renderTextBox("State", form.State, "State", "13.5vw", 30, true)}
                </Grid>
                <Grid item xs={6}>
                  {renderTextBox("Country", form.Country, "Country", "13.5vw", 30, true)}
                </Grid>
                <Grid item xs={6}>
                  {renderTextBox("Employee Range From", form.EmployeeRangeFrom, "EmployeeRangeFrom", "13.5vw", 4, false)}
                </Grid>
                <Grid item xs={6}>
                  {renderTextBox("Employee Range To", form.EmployeeRangeTo, "EmployeeRangeTo", "13.5vw", 4, false)}
                </Grid>
                <Grid item xs={12}>
                  {renderTextBox("Email ID", form.EmailID, "EmailID", "27.5vw", 50, true)}
                </Grid>
                <Grid item xs={6}>
                  {renderTextBox("Mobile Number", form.MobileNumber, "MobileNumber", "13.5vw", 10, true)}
                </Grid>
                <Grid item xs={6}>
                  {renderTextBox("Discount %", form.DiscountPercent, "DiscountPercent", "13.5vw", 5, true)}
                </Grid>
                <Grid item xs={12}>
                  {renderTextBox("Discount Notes", form.DiscountNotes, "DiscountNotes", "27.5vw", 100, false)}
                </Grid>
              </Grid>
              <Stack
                spacing={2}
                direction="row"
                sx={{ backgroundColor: "#faf9f9" }}
                alignItems="center"
                justifyContent="center"
                height="9vh"
              >
                <Button variant="outlined" size="small" id="eMed_Config_btn" onClick={ClearData}>
                  Clear
                </Button>
                <Button variant="contained" size="small" id="eMed_Config_btn" onClick={validation}>
                  Save
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      {isDeleteClicked && (
        <DeletePopup
          DeletTitle={`Delete ${SelectedList?.company_name ?? "-"}?`}
          DeleteNotify="This will affect related settings."
          deleteAlertPopupClose={popupClose}
          removeData={DeleteCorporate}
        />
      )}
    </Box>
  );
}
